import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FolderKanban, LayoutGrid, Target, Zap, FileText, Users, GraduationCap, Home as HomeIcon,
  Briefcase, Wallet, Heart, BookOpen, ClipboardList, Trash2, Shield, Settings as SettingsIcon,
  ExternalLink, Search, Archive,
} from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../lib/AppContext";

/**
 * Pagina /menu — "Altro" — griglia sezioni non-tab per mobile.
 * Sostituisce la sidebar quando il tab bar è visibile.
 */
export default function MobileMenu() {
  const { config } = useApp();
  const [query, setQuery] = useState("");
  const [badges, setBadges] = useState({});

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const b = await api.mobileBadges();
        if (!cancel) setBadges(b);
      } catch {}
    })();
    return () => { cancel = true; };
  }, []);

  const sections = [
    { id: "cicli",       label: "Cicli",              icon: ClipboardList, to: "/tasks", color: "#95BAEF", badge: badges.oggi },
    { id: "progetti",    label: "Progetti",           icon: FolderKanban,  to: "/projects", color: "#A9C8F1" },
    { id: "aree",        label: "Aree",               icon: LayoutGrid,    to: "/life-areas", color: "#C3DAF4" },
    { id: "mete",        label: "Mete",               icon: Target,        to: "/mete", color: "#F5C24A" },
    { id: "azioni",      label: "Azioni",             icon: Zap,           to: "/azioni", color: "#8FBF8F" },
    { id: "documenti",   label: "Documenti",          icon: FileText,      to: "/documenti", color: "#95BAEF", badge: badges.quick_access },
    { id: "scadenze",    label: "Scadenze",           icon: ClipboardList, to: "/deadlines", color: "#F5C24A" },
    { id: "cestino",     label: "Cestino",            icon: Trash2,        to: "/tasks/trash", color: "#8497aa" },
    { id: "sicurezza",   label: "Sicurezza",          icon: Shield,        to: "/settings/security", color: "#A9C8F1" },
    { id: "impostazioni",label: "Impostazioni",       icon: SettingsIcon,  to: "/settings/profile", color: "#4A6078" },
  ];

  const filtered = query.trim()
    ? sections.filter((s) => s.label.toLowerCase().includes(query.toLowerCase()))
    : sections;

  return (
    <div className="min-h-dvh md:hidden" data-testid="mobile-menu-page" style={{ paddingBottom: "calc(64px + env(safe-area-inset-bottom) + 16px)" }}>
      <header className="sticky top-0 z-30 bg-white border-b px-4 py-3 flex items-center gap-3" style={{ borderColor: "var(--pxos-border)" }}>
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" style={{ color: "var(--pxos-text-dark)" }} />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca sezione…"
            className="w-full pl-9 pr-3 py-2 rounded-full border text-sm"
            style={{ borderColor: "var(--pxos-border)", background: "var(--pxos-primary-light)33" }}
            data-testid="mobile-menu-search"
          />
        </div>
        <Link to="/settings/profile" className="p-2 rounded-full hover:bg-gray-100" aria-label="Vai a Impostazioni" data-testid="mobile-menu-settings">
          <SettingsIcon size={20} style={{ color: "var(--pxos-text-dark)" }} />
        </Link>
      </header>

      <section className="px-4 pt-4">
        <h1 className="font-display text-2xl font-semibold" style={{ color: "var(--pxos-text-dark)" }}>Menu</h1>
        <p className="text-xs opacity-70 mt-1" style={{ color: "var(--pxos-text-dark)" }}>
          Tutte le sezioni di {config?.app_name || "Agenda Push"}
        </p>

        <div className="grid grid-cols-2 gap-3 mt-5">
          {filtered.map((s) => (
            <Link
              key={s.id}
              to={s.to}
              className="relative rounded-2xl p-4 border transition active:scale-95 flex flex-col items-start gap-3 min-h-[112px]"
              style={{
                borderColor: "#E5E7EB",
                background: `linear-gradient(135deg, ${s.color}22 0%, #FFFFFF 100%)`,
              }}
              data-testid={`mobile-menu-${s.id}`}
            >
              <div className="h-10 w-10 rounded-full grid place-items-center" style={{ background: s.color + "33" }}>
                <s.icon size={20} style={{ color: s.color }} />
              </div>
              <div>
                <div className="text-sm font-semibold leading-tight" style={{ color: "var(--pxos-text-dark)" }}>
                  {s.label}
                </div>
              </div>
              {s.badge > 0 && (
                <span className="absolute top-3 right-3 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center">
                  {s.badge > 9 ? "9+" : s.badge}
                </span>
              )}
            </Link>
          ))}
        </div>

        <div className="mt-6 rounded-2xl border p-4" style={{ borderColor: "var(--pxos-border)" }}>
          <div className="text-[10px] uppercase tracking-widest opacity-60 mb-2" style={{ color: "var(--pxos-text-dark)" }}>
            Collegamenti esterni
          </div>
          <a
            href="https://kersystem.it"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50"
            data-testid="mobile-menu-external-kersystem"
          >
            <div className="h-8 w-8 rounded-full grid place-items-center bg-gray-100">
              <ExternalLink size={16} style={{ color: "var(--pxos-text-dark)" }} />
            </div>
            <span className="text-sm font-medium" style={{ color: "var(--pxos-text-dark)" }}>KERSystem</span>
            <ExternalLink size={12} className="opacity-50 ml-auto" />
          </a>
        </div>
      </section>
    </div>
  );
}
